const FRONT_BANNER_V2_REVALIDATE = 60;
const DEFAULT_DESKTOP_HEIGHT = 640;
const DEFAULT_TABLET_HEIGHT = 520;
const DEFAULT_MOBILE_HEIGHT = 420;
const DEFAULT_DESKTOP_HEIGHT_VH = 72;
const DEFAULT_TABLET_HEIGHT_VH = 60;
const DEFAULT_MOBILE_HEIGHT_VH = 58;
const DEFAULT_DESKTOP_MIN_HEIGHT = 520;
const DEFAULT_TABLET_MIN_HEIGHT = 420;
const DEFAULT_MOBILE_MIN_HEIGHT = 360;
const DEFAULT_CONTENT_WIDTH = 520;
const DEFAULT_CONTENT_WIDTH_UNIT = "px";
const DEFAULT_CONTENT_ANIMATION_DURATION = 600;
const DEFAULT_CONTENT_POSITION = "center-left";
const DEFAULT_OVERLAY_SCOPE = "content";
const DEFAULT_OVERLAY_COLOR = "#0f172a";
const DEFAULT_OVERLAY_OPACITY = 42;
const DEFAULT_AUTOPLAY_INTERVAL = 5000;
const MIN_AUTOPLAY_INTERVAL = 1000;
const MAX_AUTOPLAY_INTERVAL = 30000;

export type FrontBannerV2ContentPosition =
	| "top-left"
	| "center-left"
	| "center"
	| "center-right"
	| "bottom-left"
	| "bottom-center"
	| "bottom-right";

export interface FrontBannerV2Image {
	id?: string | number;
	file_id?: string | number;
	url?: string;
	name?: string;
	type?: string;
	alt?: string;
}

export interface FrontBannerV2LiItem {
	text: string;
	wrap?: boolean;
}

export type FrontBannerV2ContentAnimation = "none" | "fade" | "slide-up" | "slide-left" | "zoom-in";

export type FrontBannerV2ContentBlockType = "heading" | "text" | "li" | "button";

interface FrontBannerV2ContentBlockBase {
	id: string;
	type: FrontBannerV2ContentBlockType;
}

export interface FrontBannerV2HeadingBlock extends FrontBannerV2ContentBlockBase {
	type: "heading";
	text: string;
	color?: string;
	font_size?: string;
}

export interface FrontBannerV2TextBlock extends FrontBannerV2ContentBlockBase {
	type: "text";
	text: string;
	color?: string;
	font_size?: string;
}

export interface FrontBannerV2LiBlock extends FrontBannerV2ContentBlockBase {
	type: "li";
	items: FrontBannerV2LiItem[];
	color?: string;
	font_size?: string;
}

export interface FrontBannerV2ButtonBlock extends FrontBannerV2ContentBlockBase {
	type: "button";
	text: string;
	href: string;
	variant: "primary" | "secondary";
	full_width?: boolean;
	bg_color?: string;
	text_color?: string;
	font_size?: string;
}

export type FrontBannerV2ContentBlock =
	| FrontBannerV2HeadingBlock
	| FrontBannerV2TextBlock
	| FrontBannerV2LiBlock
	| FrontBannerV2ButtonBlock;

export type FrontBannerV2HeightMode = "fixed" | "viewport" | "fullscreen";

export interface FrontBannerV2Banner {
	id: string;
	enabled: boolean;
	sort: number;
	text_color?: string;
	content_width?: number;
	content_width_unit?: "px" | "percent";
	content_animation?: FrontBannerV2ContentAnimation;
	content_animation_duration?: number;
	overlay_scope?: "banner" | "content";
	overlay_color?: string;
	overlay_opacity?: number;
	content_blocks: FrontBannerV2ContentBlock[];
	content_position: FrontBannerV2ContentPosition;
	desktop_image?: FrontBannerV2Image | null;
	mobile_image?: FrontBannerV2Image | null;
}

export interface FrontBannerV2PageConfig {
	slug: string;
	desktop_height_mode?: FrontBannerV2HeightMode;
	tablet_height_mode?: FrontBannerV2HeightMode;
	mobile_height_mode?: FrontBannerV2HeightMode;
	desktop_height?: number;
	tablet_height?: number;
	mobile_height?: number;
	desktop_height_vh?: number;
	tablet_height_vh?: number;
	mobile_height_vh?: number;
	desktop_min_height?: number;
	tablet_min_height?: number;
	mobile_min_height?: number;
	autoplay_interval?: number;
	banners: FrontBannerV2Banner[];
}

export interface FrontBannerV2Payload {
	page_configs: FrontBannerV2PageConfig[];
}

const CONTENT_POSITION_VALUES: FrontBannerV2ContentPosition[] = [
	"top-left",
	"center-left",
	"center",
	"center-right",
	"bottom-left",
	"bottom-center",
	"bottom-right",
];

const CONTENT_ANIMATION_VALUES: FrontBannerV2ContentAnimation[] = [
	"none",
	"fade",
	"slide-up",
	"slide-left",
	"zoom-in",
];

const FONT_SIZE_CLASS_OPTIONS: Record<FrontBannerV2ContentBlockType, string[]> = {
	heading: [
		"text-2xl sm:text-3xl lg:text-4xl",
		"text-3xl sm:text-4xl lg:text-5xl",
		"text-4xl sm:text-5xl lg:text-6xl",
	],
	text: [
		"text-xs sm:text-sm lg:text-base",
		"text-sm sm:text-base lg:text-lg",
		"text-base sm:text-lg lg:text-xl",
	],
	li: [
		"text-[11px] sm:text-xs",
		"text-xs sm:text-sm",
		"text-sm sm:text-base",
	],
	button: [
		"text-xs sm:text-sm",
		"text-sm sm:text-base",
		"text-base sm:text-lg",
	],
};
const FONT_SIZE_CLASS_VALUES = Object.values(FONT_SIZE_CLASS_OPTIONS).flat();

const getStringValue = (value: unknown) => (typeof value === "string" ? value : "");

const getBooleanValue = (value: unknown, fallback = false) =>
	typeof value === "boolean" ? value : fallback;

const getNumberValue = (value: unknown, fallback: number) => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
		return Number(value);
	}

	return fallback;
};

const clampBannerDimension = (value: unknown, fallback: number) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(120, Math.min(2400, numeric));
};

const clampBannerViewportHeight = (value: unknown, fallback: number) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(20, Math.min(100, numeric));
};

const normalizeHeightMode = (value: unknown): FrontBannerV2HeightMode =>
	value === "viewport" || value === "fullscreen" ? value : "fixed";

const clampAutoplayInterval = (value: unknown, fallback = DEFAULT_AUTOPLAY_INTERVAL) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(MIN_AUTOPLAY_INTERVAL, Math.min(MAX_AUTOPLAY_INTERVAL, numeric));
};

const clampContentWidth = (value: unknown, unit: NonNullable<FrontBannerV2Banner["content_width_unit"]>) => {
	const fallback = unit === "percent" ? 48 : DEFAULT_CONTENT_WIDTH;
	const numeric = getNumberValue(value, fallback);

	if (unit === "percent") {
		return Math.max(20, Math.min(100, numeric));
	}

	return Math.max(240, Math.min(960, numeric));
};

const clampAnimationDuration = (value: unknown) => {
	const numeric = getNumberValue(value, DEFAULT_CONTENT_ANIMATION_DURATION);
	return Math.max(200, Math.min(2000, numeric));
};

const normalizeFontSize = (value: unknown, type: FrontBannerV2ContentBlockType) => {
	if (typeof value === "string" && FONT_SIZE_CLASS_VALUES.includes(value)) {
		return value;
	}

	const numeric = getNumberValue(value, 0);
	if (!numeric) {
		return undefined;
	}

	const options = FONT_SIZE_CLASS_OPTIONS[type];
	if (numeric <= 14) {
		return options[0];
	}
	if (numeric <= 22) {
		return options[1];
	}
	return options[2];
};

const clampOpacity = (value: unknown, fallback = DEFAULT_OVERLAY_OPACITY) => {
	const numeric = getNumberValue(value, fallback);
	return Math.max(0, Math.min(100, numeric));
};

const normalizeContentWidthUnit = (
	value: unknown,
): NonNullable<FrontBannerV2Banner["content_width_unit"]> => (value === "percent" ? "percent" : "px");

const normalizeContentAnimation = (value: unknown): FrontBannerV2ContentAnimation =>
	CONTENT_ANIMATION_VALUES.includes(value as FrontBannerV2ContentAnimation)
		? (value as FrontBannerV2ContentAnimation)
		: "none";

const normalizeLiItem = (value: unknown): FrontBannerV2LiItem => ({
	text: getStringValue((value as FrontBannerV2LiItem | undefined)?.text).trim(),
	wrap: getBooleanValue((value as FrontBannerV2LiItem | undefined)?.wrap),
});

const normalizeImage = (value: unknown): FrontBannerV2Image | null => {
	if (!value || typeof value !== "object") {
		return null;
	}

	const image = value as FrontBannerV2Image;
	const normalizedImage = {
		id: image.id,
		file_id: image.file_id,
		url: getStringValue(image.url).trim(),
		name: getStringValue(image.name).trim(),
		type: getStringValue(image.type).trim(),
		alt: getStringValue(image.alt).trim(),
	};

	if (!normalizedImage.url && !normalizedImage.file_id && !normalizedImage.id) {
		return null;
	}

	return normalizedImage;
};

const normalizeContentBlock = (value: unknown, index: number): FrontBannerV2ContentBlock | null => {
	if (!value || typeof value !== "object") {
		return null;
	}

	const raw = value as Partial<FrontBannerV2ContentBlock>;
	const id = getStringValue(raw.id).trim() || `block-${index}`;

	switch (raw.type) {
		case "heading": {
			const text = getStringValue((raw as FrontBannerV2HeadingBlock).text).trim();
			if (!text) {
				return null;
			}

			return {
				id,
				type: "heading",
				text,
				color: getStringValue((raw as FrontBannerV2HeadingBlock).color).trim(),
				font_size: normalizeFontSize((raw as FrontBannerV2HeadingBlock).font_size, "heading"),
			};
		}
		case "text": {
			const text = getStringValue((raw as FrontBannerV2TextBlock).text).trim();
			if (!text) {
				return null;
			}

			return {
				id,
				type: "text",
				text,
				color: getStringValue((raw as FrontBannerV2TextBlock).color).trim(),
				font_size: normalizeFontSize((raw as FrontBannerV2TextBlock).font_size, "text"),
			};
		}
		case "li": {
			const items = Array.isArray((raw as FrontBannerV2LiBlock).items)
				? (raw as FrontBannerV2LiBlock).items.map(normalizeLiItem).filter((item) => Boolean(item.text))
				: [];

			if (items.length === 0) {
				return null;
			}

			return {
				id,
				type: "li",
				items,
				color: getStringValue((raw as FrontBannerV2LiBlock).color).trim(),
				font_size: normalizeFontSize((raw as FrontBannerV2LiBlock).font_size, "li"),
			};
		}
		case "button": {
			const button = raw as FrontBannerV2ButtonBlock;
			const text = getStringValue(button.text).trim();
			const href = getStringValue(button.href).trim();

			if (!text || !href) {
				return null;
			}

			return {
				id,
				type: "button",
				text,
				href,
				variant: button.variant === "secondary" ? "secondary" : "primary",
				full_width: typeof button.full_width === "boolean" ? button.full_width : button.variant !== "secondary",
				bg_color: getStringValue(button.bg_color).trim(),
				text_color: getStringValue(button.text_color).trim(),
				font_size: normalizeFontSize(button.font_size, "button"),
			};
		}
		default:
			return null;
	}
};

const normalizeBanner = (value: unknown, index: number): FrontBannerV2Banner | null => {
	if (!value || typeof value !== "object") {
		return null;
	}

	const banner = value as Partial<FrontBannerV2Banner>;
	const contentWidthUnit = normalizeContentWidthUnit(banner.content_width_unit);
	const desktopImage = normalizeImage(banner.desktop_image);
	const mobileImage = normalizeImage(banner.mobile_image);
	const contentBlocks = Array.isArray(banner.content_blocks)
		? banner.content_blocks
				.map((block, blockIndex) => normalizeContentBlock(block, blockIndex))
				.filter((block): block is FrontBannerV2ContentBlock => Boolean(block))
		: [];

	if (!desktopImage && !mobileImage && contentBlocks.length === 0) {
		return null;
	}

	return {
		id: getStringValue(banner.id).trim() || `banner-${index}`,
		enabled: getBooleanValue(banner.enabled, true),
		sort: Math.max(0, getNumberValue(banner.sort, index + 1)),
		text_color: getStringValue(banner.text_color).trim(),
		content_width: clampContentWidth(banner.content_width, contentWidthUnit),
		content_width_unit: contentWidthUnit,
		content_animation: normalizeContentAnimation(banner.content_animation),
		content_animation_duration: clampAnimationDuration(banner.content_animation_duration),
		overlay_scope: banner.overlay_scope === "banner" ? "banner" : DEFAULT_OVERLAY_SCOPE,
		overlay_color: getStringValue(banner.overlay_color).trim() || DEFAULT_OVERLAY_COLOR,
		overlay_opacity: clampOpacity(banner.overlay_opacity),
		content_blocks: contentBlocks,
		content_position: CONTENT_POSITION_VALUES.includes(banner.content_position as FrontBannerV2ContentPosition)
			? (banner.content_position as FrontBannerV2ContentPosition)
			: DEFAULT_CONTENT_POSITION,
		desktop_image: desktopImage,
		mobile_image: mobileImage,
	};
};

const normalizePageConfig = (value: unknown): FrontBannerV2PageConfig | null => {
	if (!value || typeof value !== "object") {
		return null;
	}

	const pageConfig = value as Partial<FrontBannerV2PageConfig>;
	const slug = getStringValue(pageConfig.slug).trim();
	const rawBanners = Array.isArray(pageConfig.banners) ? pageConfig.banners : [];
	const firstBanner = rawBanners[0] as any;
	const banners = Array.isArray(pageConfig.banners)
		? pageConfig.banners
				.map((banner, index) => normalizeBanner(banner, index))
				.filter((banner): banner is FrontBannerV2Banner => Boolean(banner))
				.sort((left, right) => left.sort - right.sort)
		: [];

	if (!slug) {
		return null;
	}

	return {
		slug,
		desktop_height_mode: normalizeHeightMode(pageConfig.desktop_height_mode),
		tablet_height_mode: normalizeHeightMode(pageConfig.tablet_height_mode),
		mobile_height_mode: normalizeHeightMode(pageConfig.mobile_height_mode),
		desktop_height: clampBannerDimension(
			pageConfig.desktop_height ?? firstBanner?.desktop_height,
			DEFAULT_DESKTOP_HEIGHT,
		),
		tablet_height: clampBannerDimension(
			pageConfig.tablet_height ?? firstBanner?.tablet_height,
			DEFAULT_TABLET_HEIGHT,
		),
		mobile_height: clampBannerDimension(
			pageConfig.mobile_height ?? firstBanner?.mobile_height,
			DEFAULT_MOBILE_HEIGHT,
		),
		desktop_height_vh: clampBannerViewportHeight(pageConfig.desktop_height_vh, DEFAULT_DESKTOP_HEIGHT_VH),
		tablet_height_vh: clampBannerViewportHeight(pageConfig.tablet_height_vh, DEFAULT_TABLET_HEIGHT_VH),
		mobile_height_vh: clampBannerViewportHeight(pageConfig.mobile_height_vh, DEFAULT_MOBILE_HEIGHT_VH),
		desktop_min_height: clampBannerDimension(pageConfig.desktop_min_height, DEFAULT_DESKTOP_MIN_HEIGHT),
		tablet_min_height: clampBannerDimension(pageConfig.tablet_min_height, DEFAULT_TABLET_MIN_HEIGHT),
		mobile_min_height: clampBannerDimension(pageConfig.mobile_min_height, DEFAULT_MOBILE_MIN_HEIGHT),
		autoplay_interval: clampAutoplayInterval(pageConfig.autoplay_interval),
		banners,
	};
};

const tryParseJson = (value: unknown) => {
	if (typeof value !== "string") {
		return value;
	}

	try {
		return JSON.parse(value);
	} catch (_error) {
		return null;
	}
};

export const extractFrontBannerPayload = (response: unknown): FrontBannerV2Payload => {
	const candidates = [
		(response as any)?.detail?.ret,
		(response as any)?.detail?.data,
		(response as any)?.detail,
		(response as any)?.ret,
		(response as any)?.data,
		response,
	];

	for (const candidate of candidates) {
		const parsed = tryParseJson(candidate);
		if (parsed && typeof parsed === "object" && Array.isArray((parsed as any).page_configs)) {
			return normalizeFrontBannerPayload(parsed as FrontBannerV2Payload);
		}
	}

	return { page_configs: [] };
};

export const normalizeFrontBannerPayload = (payload: FrontBannerV2Payload): FrontBannerV2Payload => ({
	page_configs: Array.isArray(payload.page_configs)
		? payload.page_configs
				.map(normalizePageConfig)
				.filter((pageConfig): pageConfig is FrontBannerV2PageConfig => Boolean(pageConfig))
		: [],
});

export const isRenderableFrontBanner = (banner: FrontBannerV2Banner) =>
	banner.enabled !== false && Boolean(banner.desktop_image?.url || banner.mobile_image?.url || banner.content_blocks.length > 0);

export const hasRenderableFrontBannerPageConfig = (pageConfig: FrontBannerV2PageConfig | null | undefined) =>
	Boolean(pageConfig?.banners?.some(isRenderableFrontBanner));

const getFrontBannerUrl = (lang: string) => {
	const baseUrl = process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL;
	if (!baseUrl) {
		return null;
	}

	return `${baseUrl}/website/front_banner_management/v2?lang=${encodeURIComponent(lang)}`;
};

export async function fetchFrontBannerV2(lang: string): Promise<FrontBannerV2Payload | null> {
	const url = getFrontBannerUrl(lang);
	if (!url) {
		return null;
	}

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			next: { revalidate: FRONT_BANNER_V2_REVALIDATE },
		});

		if (!response.ok) {
			return null;
		}

		return extractFrontBannerPayload(await response.json());
	} catch (_error) {
		return null;
	}
}

export async function fetchFrontBannerV2Client(lang: string): Promise<FrontBannerV2Payload | null> {
	const url = getFrontBannerUrl(lang);
	if (!url) {
		return null;
	}

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			return null;
		}

		return extractFrontBannerPayload(await response.json());
	} catch (_error) {
		return null;
	}
}
